import { useEffect, useMemo, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../context/authContext";
import styles from "./PortalSubscriptionForm.module.css";
import Button from "../../UI/Button/Button";
import FeedbackModal from "../../UI/FeedbackModal/FeedbackModal";
import {
  createSubscriptionPlan,
  uploadSubscriptionPlanImage,
} from "../../../services/subscription-plan-service";

const TIER_PREFIX = {
  Basic: "PLBSC",
  Premium: "PLPRM",
  Enterprise: "PLENT",
};

function generateProjectId(tier) {
  const prefix = TIER_PREFIX[tier] ?? "PLUNK";
  const now = new Date();
  const yy = String(now.getFullYear()).slice(-2);
  const dd = String(now.getDate()).padStart(2, "0");
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const randomNum = Math.floor(Math.random() * 9000) + 1000;
  return `${prefix}${yy}${dd}${mm}${randomNum}`;
}

function todayISO() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

export default function PortalSubscriptionForm() {
  const { t } = useTranslation("common");
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const projectImageInputRef = useRef(null);
  const [projectImagePreview, setProjectImagePreview] = useState("");
  const [projectImageFile, setProjectImageFile] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState("info");
  const [modalTitle, setModalTitle] = useState("");
  const [modalMessage, setModalMessage] = useState("");

  const websiteTypeOptions = [
    { key: "ecommerce", value: "E-commerce / Online Store" },
    { key: "marketplace", value: "Marketplace" },
    { key: "business", value: "Business / Corporate Website" },
    { key: "news", value: "News & Magazine" },
    { key: "blog", value: "Blog" },
    { key: "videoStreaming", value: "Video Streaming" },
    { key: "portfolio", value: "Portfolio Site" },
    { key: "socialNetworks", value: "Social Networks" },
    { key: "communityForums", value: "Community Forums" },
    { key: "elearning", value: "E-learning Platform" },
    { key: "healthFitness", value: "Health & Fitness" },
    {
      key: "medicalService",
      value: "Doctor Appointment / Medical Service Sites",
    },
    { key: "travelBooking", value: "Travel & Booking" },
    { key: "foodDelivery", value: "Food Delivery Platforms" },
    { key: "jobBoards", value: "Job Boards & Career Sites" },
    { key: "banking", value: "Banking & Financial Institutions" },
    { key: "investment", value: "Investment Platforms" },
    { key: "nonprofit", value: "Nonprofit / Charity Websites" },
    { key: "appLanding", value: "App Landing Pages" },
    { key: "saasProduct", value: "SaaS Product Websites" },
  ];

  const [formData, setFormData] = useState({
    clientEmail: "",
    projectType: "Web Application",
    projectTier: "Basic",
    tier: "Basic",
    projectId: generateProjectId("Basic"),
    websiteType: websiteTypeOptions[0].value,
    product: "",
    amount: "",
    currency: user?.currency || "RON",
    renewalDate: todayISO(),
    status: "inactive",
    autoRenew: true,
    projectImage: "",
    scope: true,
    projectUrl: "",
  });

  useEffect(() => {
    setFormData((p) => ({ ...p, renewalDate: todayISO() }));
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "projectTier") {
      const newTier = value;
      setFormData((p) => ({
        ...p,
        projectTier: newTier,
        tier: newTier,
        projectId: generateProjectId(newTier),
      }));
      return;
    }

    setFormData((p) => ({
      ...p,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleProjectImageUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/webp",
      "image/gif",
    ];
    if (!allowedTypes.includes(file.type)) {
      setModalType("error");
      setModalTitle(
        t("forms.portalSubscription.modals.uploadFailed") || "Upload Failed",
      );
      setModalMessage(
        "Please select a valid image file (JPEG, PNG, WebP, or GIF)",
      );
      setModalOpen(true);
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setModalType("error");
      setModalTitle(
        t("forms.portalSubscription.modals.uploadFailed") || "Upload Failed",
      );
      setModalMessage("File size must be less than 10MB");
      setModalOpen(true);
      return;
    }

    setProjectImageFile(file);
    const previewUrl = URL.createObjectURL(file);
    setProjectImagePreview(previewUrl);

    if (projectImageInputRef.current) {
      projectImageInputRef.current.value = "";
    }
  };

  const resetForm = () => {
    const defaultTier = "Basic";
    setFormData({
      clientEmail: "",
      projectType: "Web Application",
      projectTier: defaultTier,
      tier: defaultTier,
      projectId: generateProjectId(defaultTier),
      websiteType: websiteTypeOptions[0].value,
      product: "",
      amount: "",
      currency: user?.currency || "RON",
      renewalDate: todayISO(),
      status: "inactive",
      autoRenew: true,
      projectImage: "",
      scope: true,
      projectUrl: "",
    });
    setProjectImagePreview("");
    setProjectImageFile(null);
    if (projectImageInputRef.current) {
      projectImageInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!isValidEmail(formData.clientEmail)) {
      setModalType("error");
      setModalTitle(t("forms.portalSubscription.modals.invalidEmail"));
      setModalMessage(t("forms.portalSubscription.modals.invalidEmailMessage"));
      setModalOpen(true);
      setIsSubmitting(false);
      return;
    }

    try {
      let projectImageUrl = formData.projectImage;

      if (projectImageFile) {
        const websiteName =
          formData.product?.trim() ||
          formData.projectUrl?.trim() ||
          "subscription-project";
        const result = await uploadSubscriptionPlanImage(
          projectImageFile,
          websiteName,
          "main",
        );
        if (result.success && result.data?.imageUrl) {
          projectImageUrl = result.data.imageUrl;
        } else {
          throw new Error(
            result.message || "Failed to upload subscription plan image",
          );
        }
      }

      const payload = { ...formData, projectImage: projectImageUrl };
      const res = await createSubscriptionPlan(payload);

      if (!res.success) {
        throw new Error(res.error || "Request failed");
      }

      setModalType("success");
      setModalTitle(t("forms.portalSubscription.modals.subscriptionCreated"));
      setModalMessage(t("forms.portalSubscription.modals.successMessage"));
      setModalOpen(true);

      resetForm();
    } catch (err) {
      setModalType("error");
      setModalTitle(t("forms.portalSubscription.modals.creationFailed"));
      setModalMessage(
        err?.message || t("forms.portalSubscription.modals.errorMessage"),
      );
      setModalOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renewalDateLabel = useMemo(() => {
    const [y, m, d] = formData.renewalDate.split("-");
    if (!y || !m || !d) return formData.renewalDate;
    const date = new Date(Number(y), Number(m) - 1, Number(d));
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  }, [formData.renewalDate]);

  return (
    <>
      <form onSubmit={handleSubmit} className={styles.projectForm}>
        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label htmlFor="clientEmail" className={styles.label}>
              {t("forms.portalSubscription.labels.clientEmail")} *
            </label>
            <input
              type="email"
              id="clientEmail"
              name="clientEmail"
              value={formData.clientEmail}
              onChange={handleInputChange}
              className={styles.input}
              placeholder={t(
                "forms.portalSubscription.placeholders.clientEmailExample",
              )}
              required
              disabled={isSubmitting}
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="projectType" className={styles.label}>
              {t("forms.portalSubscription.labels.projectType")} *
            </label>
            <select
              id="projectType"
              name="projectType"
              value={formData.projectType}
              onChange={handleInputChange}
              className={styles.select}
              required
              disabled={isSubmitting}
            >
              <option value="Web Application">
                {t("forms.portalSubscription.options.webApplication")}
              </option>
              <option value="Mobile Application">
                {t("forms.portalSubscription.options.mobileApplication")}
              </option>
            </select>
          </div>
        </div>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label htmlFor="projectTier" className={styles.label}>
              {t("forms.portalSubscription.labels.projectTier")} *
            </label>
            <select
              id="projectTier"
              name="projectTier"
              value={formData.projectTier}
              onChange={handleInputChange}
              className={styles.select}
              required
              disabled={isSubmitting}
            >
              <option value="Basic">
                {t("forms.portalSubscription.options.basic")}
              </option>
              <option value="Premium">
                {t("forms.portalSubscription.options.premium")}
              </option>
              <option value="Enterprise">
                {t("forms.portalSubscription.options.enterprise")}
              </option>
            </select>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="projectId" className={styles.label}>
              {t("forms.portalSubscription.labels.projectId")}
            </label>
            <input
              type="text"
              id="projectId"
              name="projectId"
              value={formData.projectId}
              className={styles.input}
              readOnly
              title="Auto-generated from the Project Tier, current date, and random number for uniqueness."
              disabled={isSubmitting}
            />
          </div>
        </div>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label htmlFor="websiteType" className={styles.label}>
              {t("forms.portalSubscription.labels.websiteType")} *
            </label>
            <select
              id="websiteType"
              name="websiteType"
              value={formData.websiteType}
              onChange={handleInputChange}
              className={styles.select}
              required
              disabled={isSubmitting}
            >
              {websiteTypeOptions.map((opt) => (
                <option key={opt.key} value={opt.value}>
                  {t(`forms.clientProject.options.websiteTypes.${opt.key}`)}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="product" className={styles.label}>
              {t("forms.portalSubscription.labels.product")} *
            </label>
            <input
              type="text"
              id="product"
              name="product"
              value={formData.product}
              onChange={handleInputChange}
              className={styles.input}
              placeholder={t(
                "forms.portalSubscription.placeholders.enterProductName",
              )}
              required
              disabled={isSubmitting}
            />
          </div>
        </div>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label htmlFor="amount" className={styles.label}>
              {t("forms.portalSubscription.labels.amount")} *
            </label>
            <input
              type="number"
              id="amount"
              name="amount"
              value={formData.amount}
              onChange={handleInputChange}
              className={styles.input}
              placeholder={t(
                "forms.portalSubscription.placeholders.amountExample",
              )}
              min="0"
              step="0.01"
              required
              disabled={isSubmitting}
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="currency" className={styles.label}>
              {t("forms.portalSubscription.labels.currency")}
            </label>
            <select
              id="currency"
              name="currency"
              value={formData.currency}
              onChange={handleInputChange}
              className={styles.select}
              disabled={isSubmitting}
            >
              <option value="EUR">
                {t("forms.portalSubscription.options.eur")}
              </option>
              <option value="USD">
                {t("forms.portalSubscription.options.usd")}
              </option>
              <option value="GBP">
                {t("forms.portalSubscription.options.gbp")}
              </option>
            </select>
          </div>
        </div>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <span className={styles.label}>
              {t("forms.portalSubscription.labels.renewalDate")}
            </span>
            <div className={styles.readonlyValue}>{renewalDateLabel}</div>
            <input
              type="hidden"
              name="renewalDate"
              value={formData.renewalDate}
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="status" className={styles.label}>
              {t("forms.portalSubscription.labels.status")}
            </label>
            <select
              id="status"
              name="status"
              value={formData.status}
              onChange={handleInputChange}
              className={styles.select}
              disabled={isSubmitting}
            >
              <option value="inactive">
                {t("forms.portalSubscription.options.inactive")}
              </option>
            </select>
          </div>
        </div>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label htmlFor="projectUrl" className={styles.label}>
              {t("forms.portalSubscription.labels.projectUrl")}
            </label>
            <input
              type="url"
              id="projectUrl"
              name="projectUrl"
              value={formData.projectUrl}
              onChange={handleInputChange}
              className={styles.input}
              placeholder={t(
                "forms.portalSubscription.placeholders.projectUrlExample",
              )}
              disabled={isSubmitting}
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="projectImage" className={styles.label}>
              {t("forms.portalSubscription.labels.projectImage")}
            </label>
            <div className={styles.fileUploadContainer}>
              <label
                className={`${styles.fileInputLabel} ${isSubmitting ? styles.disabled : ""}`}
              >
                <span className={styles.fileInputText}>
                  {projectImageFile || formData.projectImage
                    ? "Change Image"
                    : "Choose Image File"}
                </span>
                <input
                  type="file"
                  id="projectImage"
                  name="projectImage"
                  ref={projectImageInputRef}
                  accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                  onChange={handleProjectImageUpload}
                  className={styles.fileInput}
                  disabled={isSubmitting}
                />
              </label>
              {(projectImagePreview || formData.projectImage) && (
                <div className={styles.imagePreviewWrapper}>
                  <img
                    src={projectImagePreview || formData.projectImage}
                    alt="Project preview"
                    className={styles.imagePreview}
                  />
                  {(projectImageFile || formData.projectImage) && (
                    <button
                      type="button"
                      onClick={() => {
                        setFormData((p) => ({ ...p, projectImage: "" }));
                        setProjectImagePreview("");
                        setProjectImageFile(null);
                        if (projectImageInputRef.current) {
                          projectImageInputRef.current.value = "";
                        }
                      }}
                      className={styles.removeImageButton}
                      disabled={isSubmitting}
                    >
                      Remove
                    </button>
                  )}
                </div>
              )}
              {!projectImagePreview && !projectImageFile && (
                <p className={styles.helperText}>
                  JPEG, PNG, WebP, GIF (max 10MB)
                </p>
              )}
            </div>
          </div>
        </div>

        <div className={styles.checkboxGroup}>
          <div className={styles.checkboxItem}>
            <input
              type="checkbox"
              id="autoRenew"
              name="autoRenew"
              checked={formData.autoRenew}
              onChange={handleInputChange}
              className={styles.checkbox}
              disabled={isSubmitting}
            />
            <label htmlFor="autoRenew" className={styles.checkboxLabel}>
              {t("forms.portalSubscription.labels.autoRenew")}
            </label>
          </div>

          <div className={styles.checkboxItem}>
            <input
              type="checkbox"
              id="scope"
              name="scope"
              checked={formData.scope}
              onChange={handleInputChange}
              className={styles.checkbox}
              disabled={isSubmitting}
            />
            <label htmlFor="scope" className={styles.checkboxLabel}>
              {t("forms.portalSubscription.labels.enableScope")}
            </label>
          </div>
        </div>

        <div className={styles.formActions}>
          <Button
            type="button"
            variant="light"
            onClick={resetForm}
            disabled={isSubmitting}
          >
            {t("forms.portalSubscription.buttons.resetForm")}
          </Button>
          <Button type="submit" variant="default" disabled={isSubmitting}>
            {isSubmitting
              ? t("forms.portalSubscription.buttons.creating")
              : t("forms.portalSubscription.buttons.createSubscriptionPlan")}
          </Button>
        </div>
      </form>

      <FeedbackModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={modalTitle}
        message={modalMessage}
        type={modalType}
        confirmText={t("forms.portalSubscription.modals.close")}
      />
    </>
  );
}
